import React, { useEffect, useState } from 'react';
import type { ToggleModalProps } from '../../components/modal/types';
import type { OptionProps, OptionTypeBase } from 'react-select';
import type { ClassReportStudentType, ReportsFilterOptionsType } from '../../pages/types';
import { useI18n } from '@whizz/react-i18n';
import { reports_filters } from '../../pages/types';
import { ChoiceGroup, Dropdown } from '../../components';
import { CustomOptionWithIcon } from '../../components/selector/components';
import type {
  ReportFilterGenderValue,
  ReportFilterGroupsValue,
  ReportFilterProgressionValue,
  ReportFilterQualityValue,
  ReportFilterStudentsValue,
  ReportFilterTagValue,
} from './ReportFilterModal.views';
import {
  ReportFilterGender,
  ReportFilterProgression,
  ReportFilterQuality,
  ReportFilterStudents,
  ReportFilterTag,
} from './ReportFilterModal.views';
import type { Tag } from '@whizz/api-model/dist/@types';

interface FilterOptionType {
  filter: ReportsFilterOptionsType;
  value: number;
  label: string;
}

export type FilterValueType =
  | ReportFilterStudentsValue
  | ReportFilterGroupsValue
  | ReportFilterQualityValue
  | ReportFilterProgressionValue
  | ReportFilterGenderValue
  | ReportFilterTagValue
  | null;

export interface ReportFilterModalProps extends Pick<ToggleModalProps, 'toggle'> {
  students: ClassReportStudentType[],
  clearFilters(): void,
  onSubmit(value: FilterValueType, showOthersData: boolean): Promise<void>,
  filter: FilterValueType,
  show: boolean,
  tags?: Tag[] | null,
}

const ReportFilterModal = (
  {
    students,
    clearFilters,
    onSubmit,
    filter,
    show,
    toggle,
    tags = [],
  }: ReportFilterModalProps,
): JSX.Element => {
  const { translate } = useI18n();
  const [ filterView, setFilterView ] = useState<FilterOptionType | null>(null);
  const [ localShowOthersData, setLocalShowOthersData ] = useState(show);

  const getOptions = (): FilterOptionType[] => {
    const arr: FilterOptionType[] = [];

    reports_filters.map((filter, index) => {
      arr.push({
        filter: filter,
        value: index,
        label: translate(`pages.REPORTS.FILTERS.MODAL.${filter}`),
      });
    });

    return arr;
  };

  useEffect(() => {
    if (filter === null) {
      setFilterView(getOptions()[0]);
    } else {
      const filterSelected = Object.keys(filter)[0];

      setFilterView(getOptions().find((option) => option.filter === filterSelected)!);
    }
  }, []);

  const handleFilterChange = (value: FilterValueType) => {
    return Promise.resolve(onSubmit(value, localShowOthersData))
      .then(() => toggle(false))
    ;
  };

  return (
    <>
      <div className={'container flex items-center justify-between pb-md border-b-2 border-header'} data-cy="report-filter-modal">
        <div className={'w-2/4'}>
          <Dropdown
            id={'filter_view'}
            label={translate('pages.REPORTS.FILTERS.MODAL.selector_label')}
            labelStyle={'bold'}
            isInline
            forceBorder
            disableSearch
            align={'space-between'}
            options={getOptions()}
            value={filterView}
            handleChange={(option: OptionTypeBase) => {
              setFilterView(getOptions().filter((filter) => filter.value === option.value)[0]);
            }}
            componentsForSelect={{
              Option: (props: OptionProps<OptionTypeBase, false>) => {
                return (
                  <CustomOptionWithIcon
                    align={'left'}
                    size={30}
                    color={'black'}
                    data-cy="custom-option"
                    {...props}
                  >
                    {props.children}
                  </CustomOptionWithIcon>
                );
              },
            }}
          />
        </div>
        <div>
          <ChoiceGroup
            id={'show_other_class_data'}
            label={translate('pages.REPORTS.FILTERS.MODAL.show_other_class_data')}
            checked={localShowOthersData}
            onChange={(event) => setLocalShowOthersData(event.target.checked)}
          />
        </div>
      </div>
      <div>
        {/* Views */}
        {filterView?.filter === 'student' && (
          <ReportFilterStudents
            students={students}
            toggle={toggle}
            initialValue={{
              student: (filter && 'student' in filter) ? filter.student : [],
              showOthersData: show,
            }}
            onSubmit={(values) => handleFilterChange({ student: values.student })}
            onClear={() => {
              clearFilters();
              toggle();
            }}
          />
        )}
        {filterView?.filter === 'gender' && (
          <ReportFilterGender
            toggle={toggle}
            initialValue={{
              gender: (filter && 'gender' in filter) ? filter.gender : null,
            }}
            onSubmit={(values) => handleFilterChange(values)}
            onClear={() => {
              clearFilters();
              toggle();
            }}
          />
        )}
        {filterView?.filter === 'quality' && (
          <ReportFilterQuality
            toggle={toggle}
            initialValue={{
              quality: (filter && 'quality' in filter) ? filter.quality : null,
            }}
            onSubmit={(values) => handleFilterChange(values)}
            onClear={() => {
              clearFilters();
              toggle();
            }}
          />
        )}
        {filterView?.filter === 'progression' && (
          <ReportFilterProgression
            toggle={toggle}
            initialValue={{
              progression: (filter && 'progression' in filter) ? filter.progression : null,
            }}
            onSubmit={(values) => handleFilterChange(values)}
            onClear={() => {
              clearFilters();
              toggle();
            }}
          />
        )}
        {filterView?.filter === 'tag' && tags && (
          <ReportFilterTag
            tags={tags}
            toggle={toggle}
            initialValue={{
              tag: (filter && 'tag' in filter) ? filter.tag : null,
            }}
            onSubmit={(values) => handleFilterChange(values)}
            onClear={() => {
              clearFilters();
              toggle();
            }}
          />
        )}
      </div>
    </>
  );
};

ReportFilterModal.displayName = 'ReportFilterModal';

export default ReportFilterModal;
